import { api } from "@/integrations/clients/carAuction/carAuctionClient";
import { MediaService } from "@/services/media/mediaService";
import { FileObject } from "@/types/shared/file";
import {
  compressImageForPreview,
  compressImageForFullSize,
} from "@/utils/imageCompression";
import { ENGINE_VIDEO_MAX_BYTES, ENGINE_VIDEO_MAX_MB } from "@/lib/engineVideo";
import { listingPostLabels } from "@/i18n";
// Image compression is now done in useImageUpload hook before upload, so no need to compress here
// BUT: For CarTest, Campaign, and News, we now create both preview and fullSize versions

export class CarTestService {
  /**
   * Add a CarTestPicture (lazy upload pattern)
   * Similar to VehicleCardService.addVehicleCardImage
   */
  static async addCarTestImage(
    carTestId: string,
    category: "exterior" | "interior" | "damage" | "service" | "tires",
    file: File,
    isThumbnail?: boolean,
    tireType?: "primary" | "secondary",
    damageComment?: string,
    frameIndex?: number,
  ): Promise<FileObject> {
    // Validate file type and size using constants
    const { SUPPORTED_IMAGE_TYPES, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } =
      await import("@/config/constants");
    const SUPPORTED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-m4v"];
    const imagesLabels = listingPostLabels().images as {
      toasts?: Record<string, string>;
      engineVideo?: Record<string, string>;
    };
    const toasts = imagesLabels.toasts ?? {};
    const engineVideoLabels = imagesLabels.engineVideo ?? {};
    const mimeBase = (file.type || "").toLowerCase().split(";")[0].trim();
    const isVideo = SUPPORTED_VIDEO_TYPES.includes(mimeBase);
    if (!SUPPORTED_IMAGE_TYPES.includes(mimeBase) && !isVideo) {
      const allowedExtensions = SUPPORTED_IMAGE_TYPES.map((t) => {
        const ext = t.split("/")[1].toUpperCase();
        return ext === "JPEG" || ext === "JPG" ? "JPEG" : ext;
      }).filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates
      throw new Error(
        (
          toasts.invalidFileTypeWithVideo ??
          "Invalid file format. Only {extensions} or a short video (mp4/mov) are allowed."
        ).replace("{extensions}", allowedExtensions.join(", ")),
      );
    }

    const maxBytes = isVideo ? ENGINE_VIDEO_MAX_BYTES : MAX_FILE_SIZE_BYTES;
    if (file.size > maxBytes) {
      throw new Error(
        isVideo
          ? (
              engineVideoLabels.tooLarge ??
              "The video is too large. Max {mb} MB."
            ).replace("{mb}", String(ENGINE_VIDEO_MAX_MB))
          : (toasts.fileTooLarge ?? "File is too large. Max size is {mb}MB.").replace(
              "{mb}",
              String(MAX_FILE_SIZE_MB),
            ),
      );
    }

    // Map frontend category to backend enum value
    const categoryMap: Record<typeof category, string> = {
      exterior: "EXTERIOR",
      interior: "INTERIOR",
      damage: "DAMAGES",
      service: "SERVICE",
      tires: "WHEELS",
    };
    const backendCategory = categoryMap[category];
    // Step 1: Compress image for both preview and fullSize (skip for engine video clips)
    let previewFile: File;
    let fullSizeFile: File;

    if (isVideo) {
      previewFile = file;
      fullSizeFile = file;
    } else {
      try {
        // Create preview version (100kb max)
        previewFile = await compressImageForPreview(file);
        // Create fullSize version (0.7mb max)
        fullSizeFile = await compressImageForFullSize(file);
      } catch (compressionError) {
        // Fallback to original file if compression fails
        previewFile = file;
        fullSizeFile = file;
      }
    }

    // Step 2: Create CarTestPicture record first (via backend endpoint)
    // We need to create the join table record first, then create Media with carTestPictureId
    const carTestPictureResponse = await api.post<{
      success: boolean;
      data: { id: string };
    }>(`/tests/${carTestId}/images`, {
      category: backendCategory, // Backend expects uppercase enum value
      isThumbnail: isThumbnail === true ? true : false, // Explicitly convert to boolean
      tireType,
      damageComment,
      ...(frameIndex !== undefined ? { frameIndex } : {}),
    });

    if (!carTestPictureResponse.success || !carTestPictureResponse.data?.id) {
      throw new Error(
        "Failed to create car test picture: Server response missing data or id",
      );
    }

    const carTestPictureId = carTestPictureResponse.data.id;

    // Step 3: Upload preview version to S3 and create Media record
    const fileExtension = file.name.split(".").pop() || "jpg";
    const previewFileName = `${file.name.replace(/\.[^/.]+$/, "")}_preview.${fileExtension}`;

    const { uploadUrl: previewUploadUrl, s3Key: previewS3Key } =
      await MediaService.requestPresignedUploadUrl(
        "carTest",
        carTestId,
        {
          originalName: previewFileName,
          mimeType: previewFile.type,
          sizeBytes: previewFile.size,
        },
        `${category.toLowerCase()}/preview`, // subPath with preview suffix
      );

    await MediaService.uploadFileToS3(previewUploadUrl, previewFile);

    const previewMediaResponse = await MediaService.createMediaAfterUpload(
      previewS3Key,
      {
        originalName: previewFileName,
        mimeType: previewFile.type,
        sizeBytes: previewFile.size,
      },
      { carTestPictureId, mediaSize: "PREVIEW" },
    );

    await MediaService.updateMediaStatus(previewMediaResponse.id, "UPLOADED");

    // Step 4: Upload fullSize version to S3 and create Media record
    const fullSizeFileName = `${file.name.replace(/\.[^/.]+$/, "")}_fullsize.${fileExtension}`;

    const { uploadUrl: fullSizeUploadUrl, s3Key: fullSizeS3Key } =
      await MediaService.requestPresignedUploadUrl(
        "carTest",
        carTestId,
        {
          originalName: fullSizeFileName,
          mimeType: fullSizeFile.type,
          sizeBytes: fullSizeFile.size,
        },
        `${category.toLowerCase()}/fullsize`, // subPath with fullsize suffix
      );

    await MediaService.uploadFileToS3(fullSizeUploadUrl, fullSizeFile);

    const fullSizeMediaResponse = await MediaService.createMediaAfterUpload(
      fullSizeS3Key,
      {
        originalName: fullSizeFileName,
        mimeType: fullSizeFile.type,
        sizeBytes: fullSizeFile.size,
      },
      { carTestPictureId, mediaSize: "FULL_SIZE" },
    );

    await MediaService.updateMediaStatus(fullSizeMediaResponse.id, "UPLOADED");

    // Step 5: Presigned URL — images use preview in grids; videos always use full-size.
    const displayMediaId = isVideo
      ? fullSizeMediaResponse.id
      : previewMediaResponse.id;
    const downloadUrl = await MediaService.getDownloadUrl(displayMediaId, 3600);

    return {
      id: carTestPictureId,
      url: downloadUrl,
      name: file.name,
      type: file.type,
      size: isVideo ? fullSizeFile.size : previewFile.size,
      mediaId: fullSizeMediaResponse.id,
      previewMediaId: isVideo ? undefined : previewMediaResponse.id,
      fullSizeMediaId: fullSizeMediaResponse.id,
    };
  }

  /**
   * Remove a CarTestPicture and its associated media
   */
  static async removeCarTestImage(
    carTestId: string,
    imageId: string,
  ): Promise<void> {
    await api.delete(`/tests/${carTestId}/images/${imageId}`);
  }

  /**
   * Reorder CarTestPictures. pictureIds = display order (first = position 0).
   */
  static async reorderCarTestImages(
    carTestId: string,
    pictureIds: string[],
  ): Promise<void> {
    await api.patch(`/tests/${carTestId}/images/reorder`, { pictureIds });
  }

  /**
   * Update CarTestPicture damage comment
   */
  static async updateCarTestImageComment(
    carTestId: string,
    imageId: string,
    comment: string,
  ): Promise<void> {
    await api.put(`/tests/${carTestId}/images/${imageId}/comment`, { comment });
  }

  /**
   * Replace CarTestPicture image (e.g. after rotation).
   * Frontend uploads directly to S3 via presigned URLs; backend only handles metadata.
   */
  static async replaceCarTestImage(
    carTestId: string,
    imageId: string,
    previewFile: File,
    fullSizeFile?: File,
  ): Promise<{
    url: string;
    previewMediaId?: string;
    fullSizeMediaId?: string;
  }> {
    const fullSize = fullSizeFile ?? previewFile;
    type UploadUrlPayload = {
      previewUploadUrl: string;
      previewS3Key: string;
      fullSizeUploadUrl: string;
      fullSizeS3Key: string;
      previewMetadata: {
        originalName: string;
        mimeType: string;
        sizeBytes: number;
      };
      fullSizeMetadata: {
        originalName: string;
        mimeType: string;
        sizeBytes: number;
      };
    };
    const uploadUrlRes = await api.post<{
      success?: boolean;
      data?: UploadUrlPayload;
    }>(`/tests/${carTestId}/images/${imageId}/replace/upload-url`, {
      previewMetadata: {
        mimeType: previewFile.type || "image/jpeg",
        sizeBytes: previewFile.size,
      },
      fullSizeMetadata: {
        mimeType: fullSize.type || "image/jpeg",
        sizeBytes: fullSize.size,
      },
    });
    const uploadUrlData: UploadUrlPayload | undefined =
      (uploadUrlRes as { data?: UploadUrlPayload }).data ??
      (uploadUrlRes as unknown as UploadUrlPayload);
    if (!uploadUrlData?.previewUploadUrl || !uploadUrlData?.fullSizeUploadUrl) {
      throw new Error("Replace upload-url response missing presigned URLs");
    }

    await Promise.all([
      MediaService.uploadFileToS3(
        uploadUrlData.previewUploadUrl,
        previewFile,
      ),
      MediaService.uploadFileToS3(
        uploadUrlData.fullSizeUploadUrl,
        fullSize,
      ),
    ]);

    type CompletePayload = {
      url: string;
      previewMediaId?: string;
      fullSizeMediaId?: string;
    };
    const completeRes = await api.post<{
      success?: boolean;
      data?: CompletePayload;
    }>(`/tests/${carTestId}/images/${imageId}/replace/complete`, {
      previewS3Key: uploadUrlData.previewS3Key,
      fullSizeS3Key: uploadUrlData.fullSizeS3Key,
      previewMetadata: uploadUrlData.previewMetadata,
      fullSizeMetadata: uploadUrlData.fullSizeMetadata,
    });
    const data: CompletePayload | undefined =
      (completeRes as { data?: CompletePayload }).data ??
      (completeRes as unknown as CompletePayload);
    const url = data?.url;
    if (!url) {
      throw new Error("Replace complete response missing url");
    }
    return {
      url,
      previewMediaId: data?.previewMediaId,
      fullSizeMediaId: data?.fullSizeMediaId,
    };
  }

  /**
   * Get CarTestPictures for a carTest, optionally filtered by category
   * @param usePreview - true = PREVIEW (smaller), false = FULL_SIZE (detail/fullscreen)
   */
  static async getCarTestImages(
    carTestId: string,
    category?: string,
    usePreview: boolean = true,
  ): Promise<
    Array<{
      id: string;
      category: string;
      frameIndex?: number;
      isThumbnail?: boolean;
      tireType?: string;
      damageComment?: string;
      position?: number;
      url: string;
      fileName: string;
      mimeType: string;
      sizeBytes: number;
      createdAt: string;
    }>
  > {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (!usePreview) params.set("usePreview", "false");
    const response = await api.get<{
      success: boolean;
      data: Array<{
        id: string;
        category: string;
        frameIndex?: number;
        position?: number;
        isThumbnail?: boolean;
        tireType?: string;
        damageComment?: string;
        url: string;
        fileName: string;
        mimeType: string;
        sizeBytes: number;
        createdAt: string;
      }>;
    }>(
      `/tests/${carTestId}/images${params.toString() ? `?${params.toString()}` : ""}`,
    );

    if (!response.success || !response.data) {
      throw new Error(
        "Failed to get car test images: Server response missing data",
      );
    }

    return response.data;
  }
}

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:5001/api/v1"; /**
 * Same-origin URL to download a car test image (for rotation). Use this instead of
 * presigned S3 URLs to avoid CORS when fetching image data in the browser.
 * @param size - 'preview' (default) or 'fullsize' – use fullsize when rotating to keep quality.
 */
export function getCarTestImageDownloadUrl(
  carTestId: string,
  imageId: string,
  size?: "preview" | "fullsize",
): string {
  const base = `${API_BASE_URL}/tests/${carTestId}/images/${imageId}/download`;
  return size === "fullsize" ? `${base}?size=fullsize` : base;
}
